import {
  INodeProperties,
  NodeParameterValueType,
  NodePropertyTypes,
} from '../../types/node-properties';

import {
  ParameterContext,
  ParameterTransformationResult,
  ParameterTransformer,
  ParameterValidationError,
  ParameterValidationResult,
  ParameterValidator,
  ResolvedParameterVisibility,
} from './types';

export class ParameterManager {
  private validators = new Map<NodePropertyTypes, ParameterValidator>();
  private transformers = new Map<NodePropertyTypes, ParameterTransformer>();

  constructor() {
    this.setupDefaultValidators();
    this.setupDefaultTransformers();
  }

  validateParameters(
    parameters: Record<string, unknown>,
    properties: INodeProperties[],
    nodeType: string
  ): ParameterValidationResult {
    const errors: ParameterValidationError[] = [];
    const context: ParameterContext = {
      nodeType,
      parameters,
      allProperties: properties,
    };

    for (const property of properties) {
      const value = parameters[property.name];

      // Check required parameters
      if (property.required && (value === undefined || value === null || value === '')) {
        errors.push({
          parameterName: property.name,
          message: `${property.displayName} is required`,
        });
        continue;
      }

      // Skip validation if parameter is not provided and not required
      if (value === undefined || value === null) {
        continue;
      }

      // Get validator for property type
      const validator = this.validators.get(property.type);
      if (validator) {
        const validationErrors = validator.validate(value, property, context);
        errors.push(...validationErrors);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  transformParameters(
    parameters: Record<string, unknown>,
    properties: INodeProperties[],
    nodeType: string
  ): ParameterTransformationResult {
    const transformed: Record<string, unknown> = {};
    const errors: ParameterValidationError[] = [];
    const context: ParameterContext = {
      nodeType,
      parameters,
      allProperties: properties,
    };

    for (const property of properties) {
      const value = parameters[property.name];

      if (value !== undefined) {
        // Get transformer for property type
        const transformer = this.transformers.get(property.type);
        if (transformer) {
          try {
            transformed[property.name] = transformer.transform(value, property, context);
          } catch (error) {
            errors.push({
              parameterName: property.name,
              message: `Transformation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
            });
            transformed[property.name] = value; // Fallback to original value
          }
        } else {
          transformed[property.name] = value;
        }
      } else if (property.default !== undefined) {
        // Use default value if no value provided
        transformed[property.name] = property.default;
      }
    }

    return { transformed, errors };
  }

  resolveParameterVisibility(
    parameters: Record<string, unknown>,
    properties: INodeProperties[]
  ): ResolvedParameterVisibility {
    const visibility: ResolvedParameterVisibility = {};

    for (const property of properties) {
      visibility[property.name] = this.isParameterVisible(property, parameters);
    }

    return visibility;
  }

  isParameterReadonly(property: INodeProperties): boolean {
    if (property.readonly) {
      return true;
    }

    return false;
  }

  private isParameterVisible(
    property: INodeProperties,
    parameters: Record<string, unknown>
  ): boolean {
    if (!property.displayOptions) {
      return true;
    }

    const { show, hide } = property.displayOptions;

    // Check show conditions
    if (show) {
      const showConditionMet = Object.entries(show).every(
        ([paramName, allowedValues]: [string, NodeParameterValueType[]]) => {
          const paramValue = parameters[paramName];
          return allowedValues.includes(paramValue as NodeParameterValueType);
        }
      );

      if (!showConditionMet) {
        return false;
      }
    }

    // Check hide conditions
    if (hide) {
      const hideConditionMet = Object.entries(hide).some(
        ([paramName, hiddenValues]: [string, NodeParameterValueType[]]) => {
          const paramValue = parameters[paramName];
          return hiddenValues.includes(paramValue as NodeParameterValueType);
        }
      );

      if (hideConditionMet) {
        return false;
      }
    }

    return true;
  }

  getDefaultParameters(properties: INodeProperties[]): Record<string, unknown> {
    const defaults: Record<string, unknown> = {};

    for (const property of properties) {
      if (property.default !== undefined) {
        defaults[property.name] = property.default;
      }
    }

    return defaults;
  }

  private setupDefaultValidators(): void {
    // String validator
    this.validators.set('string', {
      validate: (value, property) => {
        const errors: ParameterValidationError[] = [];

        if (typeof value !== 'string') {
          errors.push({
            parameterName: property.name,
            message: `${property.displayName} must be a string`,
          });
        }

        return errors;
      },
    });

    // Number validator
    this.validators.set('number', {
      validate: (value, property) => {
        const errors: ParameterValidationError[] = [];

        if (typeof value !== 'number' || isNaN(value)) {
          errors.push({
            parameterName: property.name,
            message: `${property.displayName} must be a valid number`,
          });
          return errors;
        }

        const { typeOptions } = property;
        if (typeOptions) {
          if (typeOptions.minValue !== undefined && value < typeOptions.minValue) {
            errors.push({
              parameterName: property.name,
              message: `${property.displayName} must be at least ${typeOptions.minValue}`,
            });
          }

          if (typeOptions.maxValue !== undefined && value > typeOptions.maxValue) {
            errors.push({
              parameterName: property.name,
              message: `${property.displayName} must be at most ${typeOptions.maxValue}`,
            });
          }
        }

        return errors;
      },
    });

    // Boolean validator
    this.validators.set('boolean', {
      validate: (value, property) => {
        const errors: ParameterValidationError[] = [];

        if (typeof value !== 'boolean') {
          errors.push({
            parameterName: property.name,
            message: `${property.displayName} must be a boolean`,
          });
        }

        return errors;
      },
    });

    // Options validator
    this.validators.set('options', {
      validate: (value, property) => {
        const errors: ParameterValidationError[] = [];

        if (!property.options) {
          return errors;
        }

        const validValues = property.options.map(
          (option: { value: string | number | boolean }) => option.value
        );
        if (!validValues.includes(value)) {
          errors.push({
            parameterName: property.name,
            message: `${property.displayName} must be one of: ${validValues.join(', ')}`,
          });
        }

        return errors;
      },
    });
  }

  private setupDefaultTransformers(): void {
    // String transformer
    this.transformers.set('string', {
      transform: value => String(value),
    });

    // Number transformer
    this.transformers.set('number', {
      transform: value => {
        const num = Number(value);
        if (isNaN(num)) {
          throw new Error('Invalid number format');
        }
        return num;
      },
    });

    // Boolean transformer
    this.transformers.set('boolean', {
      transform: value => Boolean(value),
    });

    // JSON transformer
    this.transformers.set('json', {
      transform: value => {
        if (typeof value === 'string') {
          try {
            return JSON.parse(value);
          } catch {
            throw new Error('Invalid JSON format');
          }
        }
        return value;
      },
    });
  }
}
